import { Controller } from "@hotwired/stimulus"

const SVG_NS = "http://www.w3.org/2000/svg"
const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * 60 * 60 * 1000
// 参加人数ライン分割閾値: 隣接バケット間がこの値を超えたら線を切る
const LINE_GAP_THRESHOLD_MS = 90 * MINUTE_MS

let chartInstanceCounter = 0

export default class extends Controller {
  static values = {
    data: Object,
    kind: String
  }

  connect() {
    this._chartId = ++chartInstanceCounter
    this.render()
    this._onResize = () => this.render()
    window.addEventListener("resize", this._onResize)
  }

  disconnect() {
    if (this._onResize) window.removeEventListener("resize", this._onResize)
    if (this._tooltip) { this._tooltip.remove(); this._tooltip = null }
  }

  #showTooltip(evt, text) {
    if (!this._tooltip) {
      this._tooltip = document.createElement("div")
      this._tooltip.className = "server-stats-chart__tooltip"
      document.body.appendChild(this._tooltip)
    }
    this._tooltip.textContent = text
    this._tooltip.style.display = "block"
    this.#positionTooltip(evt)
  }

  #positionTooltip(evt) {
    if (!this._tooltip) return
    const tip = this._tooltip
    const gap = 10
    const x = evt.clientX + gap
    const y = evt.clientY - tip.offsetHeight - gap
    tip.style.left = `${x}px`
    tip.style.top = `${Math.max(y, 4)}px`
  }

  #hideTooltip() {
    if (this._tooltip) this._tooltip.style.display = "none"
  }

  render() {
    const payload = this.dataValue || {}
    const points = Array.isArray(payload.points) ? payload.points : []
    this.element.innerHTML = ""
    if (points.length === 0) {
      const p = document.createElement("p")
      p.className = "server-stats-chart__empty"
      p.textContent = "データがありません"
      this.element.appendChild(p)
      return
    }

    const tMin = payload.range?.from ?? points[0].t
    const tMax = payload.range?.to ?? points[points.length - 1].t
    const width = Math.max(this.element.clientWidth || 600, 280)

    if (this.kindValue === "players") {
      this.element.appendChild(this.#renderPlayers(points, tMin, tMax, width))
    } else if (this.kindValue === "uptime") {
      this.element.appendChild(this.#renderUptime(points, tMin, tMax, width))
    } else if (this.kindValue === "votes") {
      this.element.appendChild(this.#renderVotes(points, tMin, tMax, width))
    }
  }

  #renderVotes(points, tMin, tMax, width) {
    const height = 210
    const pad = { t: 28, r: 16, b: 28, l: 24 }
    const plotW = width - pad.l - pad.r
    const plotH = height - pad.t - pad.b

    const rawMax = points.length ? Math.max(...points.map(p => p.votes || 0)) : 0
    const niceMax = this.#niceMax(Math.max(rawMax, 1))

    const tRange = Math.max(tMax - tMin, 1)
    const xFor = t => pad.l + ((t - tMin) / tRange) * plotW
    const yFor = v => pad.t + plotH - (v / niceMax) * plotH

    const svg = this.#svg(width, height)

    svg.appendChild(this.#el("line", {
      x1: pad.l, x2: width - pad.r,
      y1: yFor(0), y2: yFor(0),
      class: "server-stats-chart__baseline"
    }))

    const barW = Math.max((plotW / Math.max(points.length, 1)) - 2, 2)
    points.forEach(p => {
      const v = p.votes || 0
      const x = xFor(p.t)
      const y = yFor(v)
      const h = (pad.t + plotH) - y
      const rect = this.#el("rect", {
        x: (x - barW / 2).toFixed(2),
        y: y.toFixed(2),
        width: barW.toFixed(2),
        height: Math.max(h, 0).toFixed(2),
        rx: 1.5,
        class: "server-stats-chart__vote-bar"
      })
      const label = `${this.#formatDay(p.t)}\n投票 ${v}件`
      rect.addEventListener("mouseenter", evt => this.#showTooltip(evt, label))
      rect.addEventListener("mousemove", evt => this.#positionTooltip(evt))
      rect.addEventListener("mouseleave", () => this.#hideTooltip())
      svg.appendChild(rect)
    })

    // 日付ラベル: 間引いて表示
    const step = Math.max(Math.ceil(points.length / 6), 1)
    points.forEach((p, i) => {
      if (i % step !== 0 && i !== points.length - 1) return
      const d = new Date(p.t)
      svg.appendChild(this.#text(xFor(p.t), height - 8, `${d.getMonth() + 1}/${d.getDate()}`, {
        class: "server-stats-chart__axis",
        "text-anchor": "middle"
      }))
    })

    svg.appendChild(this.#text(pad.l - 4, pad.t + 10, `${niceMax}`, {
      class: "server-stats-chart__axis",
      "text-anchor": "start"
    }))

    return svg
  }

  #formatDay(t) {
    const d = new Date(t)
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${mm}/${dd}`
  }

  #renderPlayers(points, tMin, tMax, width) {
    const height = 210
    const pad = { t: 28, r: 16, b: 28, l: 16 }
    const plotW = width - pad.l - pad.r
    const plotH = height - pad.t - pad.b

    // 1時間バケットごとのピーク値に集約（バケットの開始時刻でプロット）
    const buckets = new Map()
    points.forEach(p => {
      if (!p.online || p.players == null) return
      const bucketStart = Math.floor(p.t / HOUR_MS) * HOUR_MS
      const existing = buckets.get(bucketStart)
      if (!existing || p.players > existing.players) {
        buckets.set(bucketStart, { t: bucketStart, players: p.players })
      }
    })
    const hourlyPeaks = Array.from(buckets.values()).sort((a, b) => a.t - b.t)

    const rawMax = hourlyPeaks.length ? Math.max(...hourlyPeaks.map(p => p.players)) : 0
    const niceMax = this.#niceMax(Math.max(rawMax, 1))

    const tRange = Math.max(tMax - tMin, 1)
    const xFor = t => pad.l + ((t - tMin) / tRange) * plotW
    const yFor = v => pad.t + plotH - (v / niceMax) * plotH

    const svg = this.#svg(width, height)

    const gradId = `chart-area-${this._chartId}`
    const glowId = `chart-glow-${this._chartId}`

    const defs = this.#el("defs", {})
    const grad = this.#el("linearGradient", { id: gradId, x1: 0, x2: 0, y1: 0, y2: 1 })
    grad.appendChild(this.#el("stop", { offset: "0%", class: "server-stats-chart__grad-top" }))
    grad.appendChild(this.#el("stop", { offset: "100%", class: "server-stats-chart__grad-bottom" }))
    defs.appendChild(grad)

    const filter = this.#el("filter", {
      id: glowId, x: "-10%", y: "-10%", width: "120%", height: "120%"
    })
    filter.appendChild(this.#el("feGaussianBlur", { in: "SourceGraphic", stdDeviation: 2.8, result: "blur" }))
    const merge = this.#el("feMerge", {})
    merge.appendChild(this.#el("feMergeNode", { in: "blur" }))
    merge.appendChild(this.#el("feMergeNode", { in: "SourceGraphic" }))
    filter.appendChild(merge)
    defs.appendChild(filter)
    svg.appendChild(defs)

    // 日の切替を縦の薄い破線で示す
    const firstMidnight = new Date(tMin)
    firstMidnight.setHours(0, 0, 0, 0)
    for (let t = firstMidnight.getTime(); t < tMax; t += DAY_MS) {
      if (t > tMin + HOUR_MS) {
        const x = xFor(t)
        svg.appendChild(this.#el("line", {
          x1: x, x2: x, y1: pad.t, y2: pad.t + plotH,
          class: "server-stats-chart__day-sep"
        }))
      }
    }

    // ベースライン（y=0）のみ薄く引く
    svg.appendChild(this.#el("line", {
      x1: pad.l, x2: width - pad.r,
      y1: yFor(0), y2: yFor(0),
      class: "server-stats-chart__baseline"
    }))

    // 隣接バケット間のギャップでセグメント分割
    const segments = []
    let current = []
    let prevT = null
    hourlyPeaks.forEach(p => {
      if (prevT != null && p.t - prevT > LINE_GAP_THRESHOLD_MS) {
        if (current.length) segments.push(current)
        current = []
      }
      current.push({ x: xFor(p.t), y: yFor(p.players), raw: p })
      prevT = p.t
    })
    if (current.length) segments.push(current)

    segments.forEach(seg => {
      if (seg.length >= 2) {
        const linePath = this.#smoothPath(seg)
        const areaPath = `${linePath}L${seg[seg.length - 1].x.toFixed(1)},${pad.t + plotH}L${seg[0].x.toFixed(1)},${pad.t + plotH}Z`
        svg.appendChild(this.#el("path", {
          d: areaPath, class: "server-stats-chart__area", fill: `url(#${gradId})`
        }))
        svg.appendChild(this.#el("path", {
          d: linePath, class: "server-stats-chart__line", fill: "none",
          filter: `url(#${glowId})`
        }))
      } else if (seg.length === 1) {
        svg.appendChild(this.#el("circle", {
          cx: seg[0].x.toFixed(1), cy: seg[0].y.toFixed(1), r: 2.5,
          class: "server-stats-chart__point"
        }))
      }
    })

    // ピークをハイライト＋数値ラベル
    let peak = null
    hourlyPeaks.forEach(p => { if (!peak || p.players > peak.players) peak = p })
    if (peak && peak.players > 0) {
      const px = xFor(peak.t)
      const py = yFor(peak.players)
      svg.appendChild(this.#el("circle", {
        cx: px.toFixed(1), cy: py.toFixed(1), r: 11,
        class: "server-stats-chart__peak-halo"
      }))
      svg.appendChild(this.#el("circle", {
        cx: px.toFixed(1), cy: py.toFixed(1), r: 4,
        class: "server-stats-chart__peak-dot"
      }))
      const labelY = Math.max(py - 12, pad.t + 2)
      svg.appendChild(this.#text(px, labelY, `${peak.players}人`, {
        class: "server-stats-chart__peak-label",
        "text-anchor": "middle"
      }))
    }

    // 直近のデータ点
    const latest = hourlyPeaks[hourlyPeaks.length - 1]
    if (latest && latest !== peak) {
      svg.appendChild(this.#el("circle", {
        cx: xFor(latest.t).toFixed(1), cy: yFor(latest.players).toFixed(1), r: 3.5,
        class: "server-stats-chart__latest-dot"
      }))
    }

    // 日付ラベル（各日の中央に M/D を配置）
    {
      let d = new Date(tMin)
      d.setHours(0, 0, 0, 0)
      while (d.getTime() < tMax) {
        const dayStart = Math.max(d.getTime(), tMin)
        const dayEnd = Math.min(d.getTime() + DAY_MS, tMax)
        if (dayEnd - dayStart > HOUR_MS * 2) {
          const midX = xFor((dayStart + dayEnd) / 2)
          svg.appendChild(this.#text(midX, height - 8, `${d.getMonth() + 1}/${d.getDate()}`, {
            class: "server-stats-chart__axis",
            "text-anchor": "middle"
          }))
        }
        d = new Date(d.getTime() + DAY_MS)
      }
    }

    // ホバー用マーカーとオーバーレイ
    const marker = this.#el("g", { class: "server-stats-chart__marker", style: "display:none; pointer-events:none;" })
    const markerLine = this.#el("line", {
      x1: 0, x2: 0, y1: pad.t, y2: pad.t + plotH,
      class: "server-stats-chart__marker-line"
    })
    const markerDot = this.#el("circle", { r: 5, class: "server-stats-chart__marker-dot" })
    marker.appendChild(markerLine)
    marker.appendChild(markerDot)
    svg.appendChild(marker)

    const overlay = this.#el("rect", {
      x: pad.l, y: pad.t, width: plotW, height: plotH,
      fill: "transparent",
      class: "server-stats-chart__hover-overlay"
    })
    svg.appendChild(overlay)

    if (hourlyPeaks.length > 0) {
      const onMove = evt => {
        const rect = svg.getBoundingClientRect()
        const svgX = ((evt.clientX - rect.left) / rect.width) * width
        let best = hourlyPeaks[0]
        let bestDist = Math.abs(svgX - xFor(best.t))
        for (let i = 1; i < hourlyPeaks.length; i++) {
          const d = Math.abs(svgX - xFor(hourlyPeaks[i].t))
          if (d < bestDist) { bestDist = d; best = hourlyPeaks[i] }
        }
        const cx = xFor(best.t)
        const cy = yFor(best.players)
        markerLine.setAttribute("x1", cx.toFixed(1))
        markerLine.setAttribute("x2", cx.toFixed(1))
        markerDot.setAttribute("cx", cx.toFixed(1))
        markerDot.setAttribute("cy", cy.toFixed(1))
        marker.style.display = ""
        this.#showTooltip(evt, `${this.#formatHour(best.t)}\nピーク ${best.players}人`)
      }
      overlay.addEventListener("mousemove", onMove)
      overlay.addEventListener("mouseleave", () => {
        marker.style.display = "none"
        this.#hideTooltip()
      })
    }

    return svg
  }

  #renderUptime(points, tMin, tMax, width) {
    const height = 76
    const pad = { t: 14, r: 16, b: 26, l: 16 }
    const plotW = width - pad.l - pad.r
    const plotH = height - pad.t - pad.b
    const radius = Math.min(plotH / 2, 10)

    const tRange = Math.max(tMax - tMin, 1)
    const xFor = t => pad.l + ((t - tMin) / tRange) * plotW

    const svg = this.#svg(width, height)

    const clipId = `uptime-clip-${this._chartId}`
    const defs = this.#el("defs", {})
    const clip = this.#el("clipPath", { id: clipId })
    clip.appendChild(this.#el("rect", {
      x: pad.l, y: pad.t, width: plotW, height: plotH, rx: radius, ry: radius
    }))
    defs.appendChild(clip)
    svg.appendChild(defs)

    // ベースのトラック（データがない範囲は薄い背景が覗く）
    svg.appendChild(this.#el("rect", {
      x: pad.l, y: pad.t, width: plotW, height: plotH,
      rx: radius, ry: radius,
      class: "server-stats-chart__track"
    }))

    // 角丸に収めるため clipPath でまとめる
    const barsGroup = this.#el("g", { "clip-path": `url(#${clipId})` })
    points.forEach((p, i) => {
      const next = points[i + 1]
      const segEnd = next ? next.t : tMax
      const xStart = xFor(p.t)
      const xEnd = xFor(segEnd)
      const w = Math.max(xEnd - xStart, 1)
      const cls = p.online ? "server-stats-chart__bar--online" : "server-stats-chart__bar--offline"
      const rect = this.#el("rect", {
        x: xStart.toFixed(2),
        y: pad.t,
        width: w.toFixed(2),
        height: plotH,
        class: `server-stats-chart__bar ${cls}`
      })
      const label = `${this.#formatTime(p.t)}\n${p.online ? "オンライン" : "オフライン"}${p.players != null ? ` / ${p.players}人` : ""}`
      rect.addEventListener("mouseenter", evt => this.#showTooltip(evt, label))
      rect.addEventListener("mousemove", evt => this.#positionTooltip(evt))
      rect.addEventListener("mouseleave", () => this.#hideTooltip())
      barsGroup.appendChild(rect)
    })
    svg.appendChild(barsGroup)

    // 日の切替を縦の薄い破線で示す（バーより上に重ねる）
    const firstMidnight = new Date(tMin)
    firstMidnight.setHours(0, 0, 0, 0)
    for (let t = firstMidnight.getTime(); t < tMax; t += DAY_MS) {
      if (t > tMin + HOUR_MS) {
        const x = xFor(t)
        svg.appendChild(this.#el("line", {
          x1: x, x2: x, y1: pad.t, y2: pad.t + plotH,
          class: "server-stats-chart__day-sep-overlay"
        }))
      }
    }

    // 日付ラベル（各日の中央に M/D を配置）
    let d = new Date(tMin)
    d.setHours(0, 0, 0, 0)
    while (d.getTime() < tMax) {
      const dayStart = Math.max(d.getTime(), tMin)
      const dayEnd = Math.min(d.getTime() + DAY_MS, tMax)
      if (dayEnd - dayStart > HOUR_MS * 2) {
        const midX = xFor((dayStart + dayEnd) / 2)
        svg.appendChild(this.#text(midX, height - 8, `${d.getMonth() + 1}/${d.getDate()}`, {
          class: "server-stats-chart__axis",
          "text-anchor": "middle"
        }))
      }
      d = new Date(d.getTime() + DAY_MS)
    }

    return svg
  }

  // 24時間レンジなら「HH:00」、それ以上なら「M/D」表記
  #appendTimeLabels(svg, tMin, tMax, xFor, width, height) {
    const spanMs = tMax - tMin
    const spanHours = spanMs / (60 * MINUTE_MS)
    const ticks = 6
    const labelForTime = t => {
      const d = new Date(t)
      const HH = String(d.getHours()).padStart(2, "0")
      const MM = String(d.getMinutes()).padStart(2, "0")
      if (spanHours <= 1.5) return `${HH}:${MM}`
      if (spanHours <= 36) return `${HH}:00`
      return `${d.getMonth() + 1}/${d.getDate()}`
    }
    for (let i = 0; i <= ticks; i++) {
      const t = tMin + (i / ticks) * spanMs
      const x = xFor(t)
      svg.appendChild(this.#text(x, height - 6, labelForTime(t), {
        class: "server-stats-chart__axis",
        "text-anchor": "middle"
      }))
    }
  }

  #formatTime(t) {
    const d = new Date(t)
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    const HH = String(d.getHours()).padStart(2, "0")
    const MM = String(d.getMinutes()).padStart(2, "0")
    return `${mm}/${dd} ${HH}:${MM}`
  }

  #formatHour(t) {
    const d = new Date(t)
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    const HH = String(d.getHours()).padStart(2, "0")
    return `${mm}/${dd} ${HH}:00`
  }

  // 単調三次補間でオーバーシュートのない滑らかな曲線を生成
  #smoothPath(pts) {
    const n = pts.length
    if (n === 1) return `M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`
    const dx = new Array(n - 1)
    const slope = new Array(n - 1)
    for (let i = 0; i < n - 1; i++) {
      dx[i] = pts[i + 1].x - pts[i].x
      slope[i] = (pts[i + 1].y - pts[i].y) / dx[i]
    }
    const tan = new Array(n)
    tan[0] = slope[0]
    tan[n - 1] = slope[n - 2]
    for (let i = 1; i < n - 1; i++) {
      tan[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2
    }
    for (let i = 0; i < n - 1; i++) {
      if (slope[i] === 0) { tan[i] = 0; tan[i + 1] = 0; continue }
      const a = tan[i] / slope[i]
      const b = tan[i + 1] / slope[i]
      const h = a * a + b * b
      if (h > 9) {
        const t = 3 / Math.sqrt(h)
        tan[i] = t * a * slope[i]
        tan[i + 1] = t * b * slope[i]
      }
    }
    let d = `M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`
    for (let i = 0; i < n - 1; i++) {
      const h3 = dx[i] / 3
      const c1x = pts[i].x + h3
      const c1y = pts[i].y + tan[i] * h3
      const c2x = pts[i + 1].x - h3
      const c2y = pts[i + 1].y - tan[i + 1] * h3
      d += `C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${pts[i + 1].x.toFixed(1)},${pts[i + 1].y.toFixed(1)}`
    }
    return d
  }

  #niceMax(v) {
    if (v <= 1) return 1
    const pow = Math.pow(10, Math.floor(Math.log10(v)))
    const n = v / pow
    let nice
    if (n <= 1) nice = 1
    else if (n <= 2) nice = 2
    else if (n <= 5) nice = 5
    else nice = 10
    return nice * pow
  }

  #svg(w, h) {
    return this.#el("svg", {
      viewBox: `0 0 ${w} ${h}`,
      width: "100%",
      height: h,
      class: "server-stats-chart__svg",
      preserveAspectRatio: "none"
    })
  }

  #el(name, attrs) {
    const node = document.createElementNS(SVG_NS, name)
    for (const k in attrs) node.setAttribute(k, attrs[k])
    return node
  }

  #text(x, y, content, attrs) {
    const t = this.#el("text", { x, y, ...attrs })
    t.textContent = content
    return t
  }
}
